import { defineMessage, useIntl } from 'react-intl'
import { formatPhoneAsAvoury, isValidName } from 'msp-integrations'
import type { FieldsConfiguration } from '@molecules/Field/Field.types'
import { messages } from '@organisms/RegisterForm/RegisterForm.utils'

const accountDataMessages = {
  ...messages,
  birthday: defineMessage({
    defaultMessage: 'Date of birth',
    id: 'msp.account.mydata.birthday'
  }),
  phoneMobile: defineMessage({
    defaultMessage: 'Mobile phone',
    id: 'msp.account.mydata.mobilephone'
  }),
  phoneHome: defineMessage({
    defaultMessage: 'Home phone',
    id: 'msp.account.mydata.homephone'
  }),
  phoneBusiness: defineMessage({
    defaultMessage: 'Business phone',
    id: 'msp.account.mydata.businessphone'
  }),
  currentPassword: defineMessage({
    defaultMessage: 'Current Password',
    id: 'msp.account.mydata.currentpassword'
  })
}

const REGEX_PREFIX_PHONE = /\+(\d+)-/
const DEFAULT_PREFIX = '44'

export const getPrefixPhone = (phoneNumber) => {
  if (!phoneNumber) {
    return DEFAULT_PREFIX
  }
  const prefixFound = new RegExp(REGEX_PREFIX_PHONE, 'i').exec(phoneNumber)
  return prefixFound ? prefixFound[1] : DEFAULT_PREFIX
}

export const removeInitialZero = (phoneNumber) => {
  if (!phoneNumber) return ''
  let formattedPhone = phoneNumber
  if (formattedPhone[0] === '0') {
    formattedPhone = formattedPhone.slice(1)
  }
  return formattedPhone
}

export const cleanPrefixPhone = (phoneNumber) =>
  (phoneNumber || '')
    .replaceAll(new RegExp(REGEX_PREFIX_PHONE, 'gi'), '')
    .replaceAll(/[^\d]/gi, '')

export const useRegisterField = (): FieldsConfiguration => {
  const { formatMessage } = useIntl()
  return {
    salutation: {
      id: 'salutation',
      type: 'radio',
      name: 'salutation',
      placeholder: '',
      label: formatMessage(accountDataMessages.salutation),
      options: [
        {
          id: 'mr',
          value: 'MR',
          label: formatMessage(accountDataMessages.salutationMr),
          extraClassName: 'mr-2'
        },
        {
          id: 'mrs',
          value: 'MRS',
          label: formatMessage(accountDataMessages.salutationMrs),
          extraClassName: 'ml-6 mr-2'
        }
      ],
      rules: {
        required: formatMessage({
          defaultMessage: 'Please select your salutation.',
          id: 'use_address_fields.error.please_select_salutation'
        })
      },
      defaultValues: 'MRS'
    },
    firstName: {
      id: 'firstName',
      type: 'text',
      name: 'firstName',
      placeholder: '',
      label: formatMessage(accountDataMessages.firstName),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your first name.',
          id: 'use_address_fields.error.please_enter_first_name'
        }),
        validate: (value) =>
          isValidName(value) ||
          formatMessage({
            defaultMessage: 'Field contains invalid characters',
            id: 'msp.global.form.error.valid_name'
          })
      }
    },
    lastName: {
      id: 'lastName',
      type: 'text',
      name: 'lastName',
      placeholder: '',
      label: formatMessage(accountDataMessages.lastName),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your last name.',
          id: 'use_address_fields.error.please_enter_last_name'
        }),
        validate: (value) =>
          isValidName(value) ||
          formatMessage({
            defaultMessage: 'Field contains invalid characters',
            id: 'msp.global.form.error.valid_name'
          })
      }
    },
    birthday: {
      id: 'birthday',
      type: 'date',
      name: 'birthday',
      placeholder: 'dd/mm/yyyy',
      label: formatMessage(accountDataMessages.birthday)
    },
    phoneMobile: {
      id: 'phoneMobile',
      type: 'tel',
      name: 'phoneMobile',
      placeholder: '',
      formatValue: (value) => {
        return formatPhoneAsAvoury(value || '')
      },
      label: formatMessage(accountDataMessages.phoneMobile)
    },
    phoneHome: {
      id: 'phoneHome',
      type: 'tel',
      name: 'phoneHome',
      placeholder: '',
      formatValue: (value) => {
        return formatPhoneAsAvoury(value || '')
      },
      label: formatMessage(accountDataMessages.phoneHome)
    },
    phoneBusiness: {
      id: 'phoneBusiness',
      type: 'tel',
      name: 'phoneBusiness',
      placeholder: '',
      formatValue: (value) => {
        return formatPhoneAsAvoury(value || '')
      },
      label: formatMessage(accountDataMessages.phoneBusiness)
    },
    email: {
      id: 'email',
      type: 'email',
      name: 'email',
      placeholder: '',
      label: formatMessage(accountDataMessages.email),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your email address.',
          id: 'msp.login_form.error.required_email'
        }),
        pattern: {
          value: new RegExp(
            '^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$',
            'i'
          ),
          message: formatMessage({
            defaultMessage: 'Please enter valid email address.',
            id: 'msp.login_form.error.valid_email'
          })
        }
      }
    },
    password: {
      id: 'password',
      type: 'password',
      name: 'password',
      placeholder: '',
      label: formatMessage(accountDataMessages.password),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your password.',
          id: 'msp.login_form.error.required_password'
        })
      }
    },
    repeatPassword: {
      id: 'repeatPassword',
      type: 'password',
      name: 'repeatPassword',
      placeholder: '',
      label: formatMessage(accountDataMessages.repeatPassword),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your password.',
          id: 'msp.login_form.error.required_password'
        })
      }
    }
  }
}

export const usePasswordChangeField = (): FieldsConfiguration => {
  const { formatMessage } = useIntl()
  return {
    currentPassword: {
      id: 'currentPassword',
      type: 'password',
      name: 'currentPassword',
      placeholder: '',
      label: formatMessage(accountDataMessages.currentPassword),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your password.',
          id: 'msp.login_form.error.required_password'
        })
      }
    },
    password: {
      id: 'password',
      type: 'password',
      name: 'password',
      placeholder: '',
      label: formatMessage(accountDataMessages.password),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your password.',
          id: 'msp.login_form.error.required_password'
        })
      }
    },
    repeatPassword: {
      id: 'repeatPassword',
      type: 'password',
      name: 'repeatPassword',
      placeholder: '',
      label: formatMessage(accountDataMessages.repeatPassword),
      rules: {
        required: formatMessage({
          defaultMessage: 'Please enter your password.',
          id: 'msp.login_form.error.required_password'
        })
      }
    }
  }
}
